import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import { gql } from '@apollo/client';
import { User, AuthResponse, AuthContextType } from '../types/auth';

const LOGIN_MUTATION = gql`
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      success
      message
      error
      token
      user {
        id
        email
        firstName
        lastName
        createdAt
      }
    }
  }
`;

const ME_QUERY = gql`
  query Me {
    me {
      id
      email
      firstName
      lastName
      createdAt
    }
  }
`;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
  const [loading, setLoading] = useState(true);

  const apolloClient = useApolloClient();
  const [loginMutation] = useMutation(LOGIN_MUTATION);

  const { refetch: refetchMe } = useQuery(ME_QUERY, {
    skip: !token,
    errorPolicy: 'all',
    onCompleted: (data) => {
      if (data?.me) {
        setUser(data.me);
      }
      setLoading(false);
    },
    onError: (error) => {
      console.warn('Auth check failed:', error.message);
      const isAuthError = error.graphQLErrors?.length > 0 ||
        (error.networkError && 'statusCode' in error.networkError && (error.networkError as any).statusCode === 401);
      if (isAuthError) {
        signOut();
      }
      setLoading(false);
    }
  });

  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      apolloClient.clearStore();
    }
  }, [token, apolloClient]);

  const signOut = useCallback((): void => {
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
    apolloClient.clearStore();
  }, [apolloClient]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken' && e.newValue === null) {
        signOut();
      } else if (e.key === 'authToken' && e.newValue && !token) {
        setToken(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [token, signOut]);

  const signIn = async (email: string, password: string): Promise<AuthResponse> => {
    try {
      const { data } = await loginMutation({
        variables: {
          input: { email, password }
        }
      });

      if (data.login.success && data.login.token) {
        const authToken = data.login.token;
        localStorage.setItem('authToken', authToken);
        setToken(authToken);
        setUser(data.login.user);
        return { success: true, user: data.login.user };
      } else {
        return {
          success: false,
          error: data.login.error || data.login.message || 'Login failed'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error during login'
      };
    }
  };

  const isAuthenticated = !!token && !!user;

  const value: AuthContextType = {
    user,
    token,
    loading,
    isAuthenticated,
    signIn,
    signOut,
    refetchMe
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
